import { Dispatch, useMemo } from 'react';
import { ModalComponent, Spinner, Text } from 'ui';
import { ActivityStatusResponse, AthleteStatusDataSource } from './activity-status-util';
import { useTranslations } from 'i18n';
import { useProcessingStatusStore } from 'shared/state/processingStatusStore';
import ActivityStatusElement from './activity-status-element';
import { useLoadMoreObserver, useWindowSize } from 'shared';

const NoActivitiesProcessed: React.FC = () => {
    const { __ } = useTranslations();
    return (
        <div className="flex flex-col h-full justify-center" role="status" aria-live="polite">
            <Text variant="body-large" className="text-center">
                {__('data_processing.no_activities_processed')}
            </Text>
        </div>
    );
};

export type ModalState = { selectedActivityId: string | undefined; selectedDataSource: AthleteStatusDataSource };

interface ProcessingStatusModalProps {
    showProcessingModal: boolean;
    onClose: () => void;
    filteredStatuses: ActivityStatusResponse[];
    setExcludedIds: Dispatch<React.SetStateAction<Set<string>>>;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
}

const ProcessingStatusModal = (props: ProcessingStatusModalProps) => {
    const {
        showProcessingModal,
        onClose,
        filteredStatuses,
        setExcludedIds,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = props;

    const { __ } = useTranslations();

    const { focusedActivityId } = useProcessingStatusStore();

    const handleHideRow = (id: string) => {
        setExcludedIds((prev) => new Set(prev).add(id));
    };

    const anyActivities = filteredStatuses?.length > 0;

    const title = __('data_processing.activity_processing_history');
    const subTitle = __('data_processing.activity_processing_explanation');

    const { height } = useWindowSize();
    const bodyClassName = `relative flex flex-col items-center mb-5 ${
        anyActivities ? 'justify-start' : 'justify-center'
    }`;

    const sortedStatuses = useMemo(() => {
        if (!filteredStatuses) {
            return [];
        }
        return [...filteredStatuses].sort(
            (a, b) => new Date(b.modifiedDate).getTime() - new Date(a.modifiedDate).getTime(),
        );
    }, [filteredStatuses]);

    const autoFocusedActivity = sortedStatuses.find((s) => s.activityId === focusedActivityId);
    const autoFocusedIndex = autoFocusedActivity ? sortedStatuses.indexOf(autoFocusedActivity) : 0;

    const { observerElem } = useLoadMoreObserver({ hasNextPage, fetchNextPage });

    return (
        <ModalComponent
            id="processing-status-modal"
            openModal={showProcessingModal}
            dismissible
            position="center"
            size="4xl"
            rounding="2xl"
            bodyClassName={bodyClassName}
            bodyHeight={Math.min(460, height * 0.6)}
            title={title}
            subTitle={subTitle}
            showCancelButton={false}
            showConfirmButton={false}
            showFooter={false}
            handleClose={onClose}
            handleCancel={onClose}
            handleConfirm={onClose}
        >
            {anyActivities ? (
                <ul className="w-full list-none p-0 m-0" aria-label={title}>
                    {sortedStatuses.map((status, i) => (
                        <ActivityStatusElement
                            key={status.activityId}
                            s={status}
                            i={i}
                            autoFocusedIndex={autoFocusedIndex}
                            handleHideRow={handleHideRow}
                        />
                    ))}
                    {hasNextPage && (
                        <li className="flex justify-center py-4">
                            {/* empty unless fetching; its visibility is what triggers fetchNextPage via useLoadMoreObserver.
                            minHeight set to 1, since some browsers never report an intersection for a 0-height element */}
                            <div ref={observerElem} style={{ minHeight: 1 }}>
                                {isFetchingNextPage && (
                                    <Spinner alignment="center" testId="processing-status-load-more-spinner" />
                                )}
                            </div>
                        </li>
                    )}
                </ul>
            ) : (
                <NoActivitiesProcessed />
            )}
        </ModalComponent>
    );
};

export default ProcessingStatusModal;
